import { supabase } from '@/integrations/supabase/client';

export type ActionType = 're_engagement' | 'reassign_coach' | 'comp_something' | 'log_followup' | 'snooze';

export interface SnoozeRecord {
  snoozedAt: string;       // performed_at ISO
  snoozeUntil: string;     // payload.snooze_until ISO
  durationDays: number;    // payload.duration_days
}

export const memberActionLogService = {
  async logAction(params: {
    memberIds: string[];
    clubId: string;
    actionType: ActionType;
    payload?: Record<string, unknown>;
  }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const rows = params.memberIds.map(memberId => ({
      member_id: memberId,
      club_id: params.clubId,
      action_type: params.actionType,
      performed_by: user.id,
      performed_at: new Date().toISOString(),
      target_count: params.memberIds.length,
      payload: params.payload ?? null,
    }));

    const { error } = await supabase.from('member_action_log').insert(rows);
    if (error) console.error('[memberActionLogService.logAction]', error);
  },

  async getLastActionDates(
    clubId: string,
    memberIds: string[]
  ): Promise<Record<string, string>> {
    if (memberIds.length === 0) return {};

    const { data, error } = await supabase
      .from('member_action_log')
      .select('member_id, performed_at, action_type')
      .eq('club_id', clubId)
      .in('member_id', memberIds)
      .neq('action_type', 'snooze')
      .order('performed_at', { ascending: false });

    if (error || !data) return {};

    const result: Record<string, string> = {};
    for (const row of data) {
      if (!result[row.member_id]) {
        result[row.member_id] = row.performed_at;
      }
    }
    return result;
  },

  /**
   * Returns the most recent snooze record per member (regardless of whether it's still active).
   * Caller computes isActive/isExpired based on snoozeUntil and member.last_active.
   * An "undo" snooze is signalled by payload.undo === true — those rows are filtered out
   * so the previous snooze (if any) doesn't get returned as still-active.
   */
  async getSnoozeState(
    clubId: string,
    memberIds: string[]
  ): Promise<Record<string, SnoozeRecord>> {
    if (memberIds.length === 0) return {};

    const { data, error } = await supabase
      .from('member_action_log')
      .select('member_id, performed_at, payload')
      .eq('club_id', clubId)
      .eq('action_type', 'snooze')
      .in('member_id', memberIds)
      .order('performed_at', { ascending: false });

    if (error || !data) return {};

    const result: Record<string, SnoozeRecord> = {};
    for (const row of data) {
      // Skip if we've already recorded the most recent for this member
      if (result[row.member_id]) continue;

      const payload = (row.payload ?? {}) as Record<string, unknown>;

      // If the most recent snooze for this member is an "undo", treat as no snooze.
      // Mark with a sentinel that gets filtered out below.
      if (payload.undo === true) {
        result[row.member_id] = { snoozedAt: '', snoozeUntil: '', durationDays: 0 };
        continue;
      }

      const snoozeUntil = typeof payload.snooze_until === 'string' ? payload.snooze_until : null;
      const durationDays = typeof payload.duration_days === 'number' ? payload.duration_days : 7;
      if (!snoozeUntil) continue;

      result[row.member_id] = {
        snoozedAt: row.performed_at,
        snoozeUntil,
        durationDays,
      };
    }

    // Filter out undo sentinels
    for (const id of Object.keys(result)) {
      if (!result[id].snoozeUntil) delete result[id];
    }

    return result;
  },
};
